import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["channelEvent", "messageParser", "onMessage", "onPlayback"];
import { useChannel } from 'ably/react';
import Raven from 'raven-js';
import { useCallback, useEffect, useRef } from 'react';
import { parseMessage } from './parseMessage';
export function useAblyChannel(_ref) {
  let {
      channelEvent,
      messageParser = parseMessage,
      onMessage,
      onPlayback
    } = _ref,
    channelOptions = _objectWithoutPropertiesLoose(_ref, _excluded);
  const onMessageRef = useRef(onMessage);
  const onPlaybackRef = useRef(onPlayback);
  onMessageRef.current = onMessage;
  onPlaybackRef.current = onPlayback;
  const handleMessage = useCallback(message => {
    if (!onMessageRef.current) return;
    let parsedMessage;
    try {
      parsedMessage = messageParser(message);
    } catch (err) {
      Raven.captureException(err);
      return;
    }
    if (parsedMessage) {
      onMessageRef.current(parsedMessage);
    }
  }, [messageParser]);
  const useChannelArgs = channelEvent ? [channelOptions, channelEvent, handleMessage] : [channelOptions, handleMessage];

  // Unfortunately typescript is not able to understand the tuple type here, so we have to cast
  const channelResult = useChannel(...useChannelArgs);
  const {
    channel
  } = channelResult;

  // If an onPlayback callback is provided, fetch recent messages on
  // mount and pass them to the callback.
  useEffect(() => {
    if (channelOptions.skip || !onPlaybackRef.current) {
      return;
    }
    let channelChanged = false;
    channel.whenState('attached').then(() => {
      // If the channel changes for whatever reason, we can skip the history api
      if (channelChanged) return;
      return channel.history({
        direction: 'backwards',
        untilAttach: true
      });
    }).then(page => {
      var _page$items$flatMap, _onPlaybackRef$curren;
      // Channel may have changed/detached prior to history promise resolving,
      // e.g. when swapping between channels quickly. In this case, we should
      // ignore the history response.
      if (channelChanged || !page) return;
      const parsedMessages = (_page$items$flatMap = page.items.flatMap(message => {
        const parsedMessage = messageParser(message);
        return parsedMessage ? [parsedMessage] : [];
      })) !== null && _page$items$flatMap !== void 0 ? _page$items$flatMap : [];
      (_onPlaybackRef$curren = onPlaybackRef.current) === null || _onPlaybackRef$curren === void 0 || _onPlaybackRef$curren.call(onPlaybackRef, parsedMessages);
    }).catch(err => {
      Raven.captureException(err);
    });
    return () => {
      channelChanged = true;
    };
  }, [channel, messageParser, channelOptions.skip]);
  return channelResult;
}